/**
 * Shared scaffolding for intake typed encounter forms (Clinical Intake and
 * CoCM Intake).
 *
 * Owns the same patient/encounter/practitioner-role plumbing as
 * `useNonIntakeFormScaffold`, plus heap-only draft persistence
 * (`useHeapFormDraft`) which is intake-only. Intake forms supply their own
 * scribe pipeline via `useScribePipeline` and their own pre-fill effects.
 *
 * @agentic connections
 */

import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useMedplum, useMedplumProfile, useResource } from "@medplum/react";
import type { UseFormReturnType } from "@mantine/form";
import type {
  Encounter,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";

import { PRACTITIONER_ROLE_PAGINATION_COUNT } from "@benchmark/core/constants";
import { CONFIG } from "@/app/config";
import { useHeapFormDraft } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePractitionerRoleOptions } from "@/hooks/shared/useFHIROptions";
import { loadEncounterFormDataContext } from "@benchmark/core/services";
import { encounterToForm } from "@benchmark/core/adapters";
import { fromBundleInput } from "@benchmark/core/adapters";
import { PATIENT_TAG_MAPPINGS } from "@benchmark/core/constants";
import type {
  EncounterActivityContext,
  EncounterBundleInput,
  EncounterFormDataByType,
} from "@benchmark/core/types";

import type { EncounterFormRef } from "../EncounterFormShell";

/** Intake encounter type codes. @agentic connections */
export type IntakeEncounterCode =
  | "clinical-intake-interaction"
  | "cocm-intake-interaction"
  | "unbilled-intake-interaction";

/**
 * Whether hydrating an existing encounter should pre-mark scribe as applied
 * (`_scribeApplied = true`), which suppresses the scribe re-run on edit.
 *
 * The rule is deliberately narrow because getting it wrong is a clinical-risk
 * regression in both directions:
 *
 * - **Finalized encounter with mapped notes → true.** Re-running scribe on a
 *   finished intake re-polls AICD and leaves the Notes fields disabled (they
 *   gate on `_scribeApplied`), so hydrated content looks read-only/blank.
 * - **In-progress draft → always false.** A saved draft must ALWAYS re-offer
 *   AICD prefill. Marking a draft applied hides the prefill UI entirely and
 *   strands the clinician with no way to run AICD on the draft (the #1399
 *   regression this predicate exists to prevent).
 * - **`comment` (General Note, hidden on intake) does not count.** A
 *   comment-only encounter maps no editable note field, so scribe must still
 *   run rather than be suppressed with nothing editable shown.
 *
 * @agentic connections
 */
export const shouldMarkScribeAppliedOnHydrate = ({
  encounterStatus,
  clinicalImpressionIds,
}: {
  encounterStatus: Encounter["status"] | undefined;
  clinicalImpressionIds: Record<string, string> | undefined;
}): boolean => {
  if (encounterStatus === "in-progress") return false;
  const mappedNoteFieldKeys = Object.keys(clinicalImpressionIds ?? {}).filter(
    (key) => key !== "comment",
  );
  return mappedNoteFieldKeys.length > 0;
};

/**
 * Inputs to {@link useIntakeFormScaffold}.
 *
 * @agentic connections
 */
export type UseIntakeFormScaffoldInputs<T extends IntakeEncounterCode> = {
  type: T;
  form: UseFormReturnType<EncounterFormDataByType[T]>;
  propPatientId?: string;
  propEncounter?: Encounter;
  activityContext?: EncounterActivityContext;
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  ref: React.ForwardedRef<EncounterFormRef>;
};

/**
 * Outputs from {@link useIntakeFormScaffold}.
 *
 * @agentic connections
 */
export type UseIntakeFormScaffoldOutputs<T extends IntakeEncounterCode> = {
  patient: Patient | undefined;
  existingEncounter: Encounter | undefined;
  isEditing: boolean;
  isLoadingEdit: boolean;
  /** True when hydrating an existing encounter failed to load its related resources. */
  loadError: boolean;
  practitionerRoles: PractitionerRole[];
  profile: Practitioner | undefined;
  ConfirmationModal: ReturnType<typeof useFormProtection>["ConfirmationModal"];
  markAsClean: () => void;
  discardDraft: () => void;
  /** Original form data captured on hydrate, used for `_*Ids` bookkeeping during upsert. */
  originalFormData: EncounterFormDataByType[T] | undefined;
  /** Apply post-hydration overrides (e.g. when scribe loads a separate encounter). */
  setOriginalFormData: (data: EncounterFormDataByType[T] | undefined) => void;
};

/**
 * Wires up the intake-form scaffold: patient + encounter + practitioner-role
 * resolution, form-protection ref, draft persistence, edit hydration with
 * `_*Ids` capture for original form data tracking.
 *
 * @agentic connections
 */
export const useIntakeFormScaffold = <T extends IntakeEncounterCode>({
  type,
  form,
  propPatientId,
  propEncounter,
  activityContext,
  nextTaskOwnerOverride,
  ref,
}: UseIntakeFormScaffoldInputs<T>): UseIntakeFormScaffoldOutputs<T> => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { encounterId, patientId: urlPatientId } = useStableParams();
  const effectivePatientId = propPatientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;

  const shouldUseUrlParams =
    propEncounter === undefined && propPatientId === undefined;
  const fetchedEncounter = useResource(
    shouldUseUrlParams && encounterId
      ? { reference: `Encounter/${encounterId}` }
      : undefined,
  );
  const existingEncounter = (propEncounter ?? fetchedEncounter ?? undefined) as
    Encounter | undefined;
  const isEditing = !!existingEncounter;

  const { data: practitionerRoleResources } = usePractitionerRoleOptions({
    organization: `Organization/${CONFIG.VITE_BENCHMARK_ORGANIZATION_ID}`,
    active: "true",
    _include: "PractitionerRole:practitioner",
    _count: PRACTITIONER_ROLE_PAGINATION_COUNT,
  });
  const practitionerRoles = (practitionerRoleResources ??
    []) as PractitionerRole[];

  // In-session draft only: persist in-progress values to heap so a user who
  // navigates away from the form and back within the same session recovers
  // their input. Across-logout restoration is out of scope (see #1011) — the
  // modal can't rehydrate its open-state/context from a cold sign-in.
  const draftKey = `encounter-draft:${effectivePatientId}:${activityContext?.activityCode ?? "standalone"}`;
  const { hasDraft, restoreDraft, discardDraft } = useHeapFormDraft(
    form,
    draftKey,
    {
      enabled: !existingEncounter,
      excludeFields: [
        "_scribeInitialized",
        "_scribeApplied",
        "_topicChecks",
        "_scribeEncounterId",
        "_scribeCompositionId",
      ],
    },
  );

  const { checkProtection, markAsClean, ConfirmationModal } = useFormProtection(
    form,
    {
      enabled: true,
      blockNavigation: true,
      confirmationMessage:
        "You have unsaved changes. Are you sure you want to close without saving?",
      onDiscard: () => {
        discardDraft();
      },
    },
  );

  useImperativeHandle(ref, () => ({ checkProtection, markAsClean }), [
    checkProtection,
    markAsClean,
  ]);

  const hydratedRef = useRef(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<
    EncounterFormDataByType[T] | undefined
  >(undefined);

  useEffect(() => {
    if (!patient) return;
    // Editing: an encounter is expected (passed as a prop, or referenced in the
    // URL). Wait for it to resolve before hydrating — otherwise a patient that
    // loads first would hydrate the empty "new intake" branch and, because
    // hydration runs once (`hydratedRef`), never re-hydrate when the encounter
    // arrives, leaving a blank form on edit. Mirrors `useNonIntakeFormScaffold`.
    const encounterExpected =
      !!propEncounter || (shouldUseUrlParams && !!encounterId);
    if (encounterExpected && !existingEncounter) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    (async () => {
      try {
        if (!existingEncounter) {
          const seed = {} as Partial<EncounterBundleInput>;
          const engagementTag = patient.meta?.tag?.find(
            (t) => t.system === PATIENT_TAG_MAPPINGS.engagementStatus.system,
          );
          if (engagementTag?.code) {
            seed["patient-engagement-status"] = engagementTag.code;
          }
          const typed = fromBundleInput(type, seed as EncounterBundleInput);
          form.setValues(typed);
          form.setInitialValues(typed);
          form.resetDirty();
          setOriginalFormData(typed);

          if (hasDraft) restoreDraft();
        } else {
          const context = await loadEncounterFormDataContext(
            medplum,
            existingEncounter,
            patient,
            // Surface a read failure as a load error rather than hydrating a
            // populated encounter as a blank form.
            { throwOnError: true },
          );
          const flat = encounterToForm(context);
          const typed = fromBundleInput(type, flat);
          // Suppress the scribe re-run only where it's correct to do so — the
          // rule (and the clinical-risk reasoning behind each branch) lives in
          // `shouldMarkScribeAppliedOnHydrate` so it's pinned by a unit test.
          if (
            shouldMarkScribeAppliedOnHydrate({
              encounterStatus: existingEncounter.status,
              clinicalImpressionIds: flat._clinicalImpressionIds as
                Record<string, string> | undefined,
            })
          ) {
            (typed as Record<string, unknown>)._scribeApplied = true;
          }
          form.setValues(typed);
          form.setInitialValues(typed);
          form.resetDirty();
          setOriginalFormData(typed);
        }
      } catch (error) {
        console.error("Failed to hydrate intake encounter:", error);
        // Only an edit hydration failure is a load error worth surfacing; the
        // new-intake seed branch does not read related resources.
        if (existingEncounter) setLoadError(true);
      } finally {
        setIsLoadingEdit(false);
      }
    })();
  }, [
    patient,
    existingEncounter,
    propEncounter,
    shouldUseUrlParams,
    encounterId,
    medplum,
    form,
    hasDraft,
    restoreDraft,
    type,
  ]);

  const ownerPrefilledRef = useRef(false);
  useEffect(() => {
    if (activityContext?.activityCode) return;
    if (!practitionerRoleResources) return;
    if (ownerPrefilledRef.current) return;
    if (
      (form.values as { "followup-assigned-to"?: string })[
        "followup-assigned-to"
      ]
    ) {
      ownerPrefilledRef.current = true;
      return;
    }
    const myRole = practitionerRoles.find(
      (role) => role.practitioner?.reference === `Practitioner/${profile?.id}`,
    );
    if (myRole?.id) {
      form.setFieldValue("followup-assigned-to" as never, myRole.id as never);
      form.setFieldValue(
        "followup-assigned-to-display" as never,
        (myRole.practitioner?.display || "") as never,
      );
      ownerPrefilledRef.current = true;
    }
  }, [
    activityContext?.activityCode,
    practitionerRoles,
    practitionerRoleResources,
    profile?.id,
    form,
  ]);

  useEffect(() => {
    if (!activityContext || !nextTaskOwnerOverride) return;
    form.setFieldValue(
      "followup-assigned-to" as never,
      nextTaskOwnerOverride.ref as never,
    );
    form.setFieldValue(
      "followup-assigned-to-display" as never,
      nextTaskOwnerOverride.display as never,
    );
    // `form` is intentionally omitted: Mantine's `useForm` returns a new object
    // identity each render, so including it here re-runs this effect after the
    // `setFieldValue` re-render, looping until React throws "Maximum update
    // depth exceeded". Re-run only when the override owner actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityContext, nextTaskOwnerOverride]);

  return {
    patient,
    existingEncounter,
    isEditing,
    isLoadingEdit,
    loadError,
    practitionerRoles,
    profile,
    ConfirmationModal,
    markAsClean,
    discardDraft,
    originalFormData,
    setOriginalFormData,
  };
};
